import TemplateSection from "./TemplateSection";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { deleteVersion, publishTemplate } from "../utils/Services";
import NoteSectionPending from "./NoteSectionPending";
import { ITemplate, ITemplateVersion } from "@/utils/Types";
import { NOTE } from "@/utils/constants";
import useTemplateStore from "@/utils/TemplateStore";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import useTemplateVersionSelectorStore from "@/utils/TemplateVersionSelector";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CheckCircle, Pencil, Trash } from "@phosphor-icons/react";
import { Edit } from "lucide-react";
import ConfirmationModal from "./ConfirmationModal";
interface TemplateVersionDetailProps {
  templates: ITemplateVersion[];
  modalOpen: boolean;
  onTemplatePublished?: (response: ITemplate) => void;
  closeModal: () => void;
}

const TemplateVersionDetail: React.FC<TemplateVersionDetailProps> = ({
  templates,
  modalOpen,
  onTemplatePublished,
  closeModal,
}) => {
  const [template, setTemplate] = useState({} as ITemplateVersion);
  const [isLoading, setIsLoading] = useState(true);
  const { addTemplate } = useTemplateStore();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version");
  const { setVersion, version, setResetRefetch } =
    useTemplateVersionSelectorStore();

  const { templateId = "" } = useParams<{
    templateId: string;
  }>();

  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  const fetchTemplate = async (templateId: string, loading = true) => {
    if (loading) setIsLoading(true);
    try {
      const template = templates.find(
        (temp) => temp.version == selectedVersion
      );

      if (template) {
        setTemplate(template);
      }
    } catch (error) {
      console.error(error);
    }
    if (loading) setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);

    const res = setTimeout(async () => {
      fetchTemplate(selectedVersion!);
    }, 500);

    return () => {
      clearTimeout(res);
    };
  }, [selectedVersion, modalOpen]);

  const onSubmit = async () => {
    try {
      setVersion(template);
      closeModal();
    } catch (error: any) {}
  };

  const setTemplateActive = async () => {
    try {
      const res = await publishTemplate(
        template?.template!.id!,
        template?.id!,
        isAdmin ? isAdmin : undefined
      );

      if (res) {
        setVersion(res.published_version!);
        closeModal();
      }
    } catch (error: any) {}
  };

  const deleteTemplateVersion = async () => {
    try {
      await deleteVersion(template?.id!, isAdmin ? isAdmin : undefined);

      setResetRefetch(true);
      closeModal();
    } catch (error: any) {}
  };

  return (
    <div>
      <div className="absolute right-12 top-[15px]">
        <div className="flex items-center gap-2">
          {!template?.published && (
            <Tooltip>
              <TooltipContent side="top" className="bg-gray-600 text-white">
                <p>Set as Active</p>
              </TooltipContent>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className={"h-8 w-8"}>
                  <CheckCircle onClick={setTemplateActive} size={18} />
                </Button>
              </TooltipTrigger>
            </Tooltip>
          )}
          {template?.id !== version?.id && (
            <Tooltip>
              <TooltipContent side="top" className="bg-gray-600 text-white">
                <p>Edit</p>
              </TooltipContent>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className={"h-8 w-8"}>
                  <Pencil onClick={onSubmit} size={16} />
                </Button>
              </TooltipTrigger>
            </Tooltip>
          )}
          {templates?.length > 1 && (
            <Tooltip>
              <TooltipContent side="top" className="bg-gray-600 text-white">
                <p>Delete</p>
              </TooltipContent>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={"h-8 w-8 mr-2"}
                >
                  <Trash onClick={() => setIsModalOpen(true)} size={16} />
                </Button>
              </TooltipTrigger>
            </Tooltip>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 w-full md:h-[calc(100dvh-160px)] h-[95vh] overflow-auto">
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map((index) => (
              <div key={index} className="px-2 py-2 sm:gap-4 sm:px-6">
                <NoteSectionPending />
              </div>
            ))}
          </>
        ) : (
          <>
            {template?.content && (
              <TemplateSection
                key="content"
                title={NOTE["content"]}
                sectionKey="content"
                insertVariable={() => {}}
                showPopup={false}
                variables={[]}
              >
                <div className="text-gray-600 dark:text-gray-50 text-base border-gray-300 dark:border-gray-600 dark:bg-background">
                  {template?.content}
                </div>
              </TemplateSection>
            )}
          </>
        )}
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        heading={`Delete version ${template?.version}`}
        message="Are you sure you want to delete this version? All of your data will be permanently removed. This action cannot be undone."
        confirmAction="Delete"
        onClose={() => setIsModalOpen(false)}
        onConfirm={deleteTemplateVersion}
      />
    </div>
  );
};

export default TemplateVersionDetail;
